import { type CrowdexUIMessage, isSteerMessage } from '@crowdex/agent-contracts';
import { type ComponentProps, type CSSProperties, useEffect, useRef } from 'react';
import {
  type ComposerCommandRunner,
  ComposerCommandsProvider,
} from '@/components/desktop-shell/composer-commands-context';
import { ConversationComposer } from '@/components/desktop-shell/conversation-composer';
import { ConversationErrorBanners } from '@/components/desktop-shell/conversation-error-banners';
import { ConversationFind } from '@/components/desktop-shell/conversation-find';
import {
  ConversationHeader,
  usePanelShortcut,
} from '@/components/desktop-shell/conversation-header';
import { ConversationMessages } from '@/components/desktop-shell/conversation-messages';
import { useFloatingConversation } from '@/components/desktop-shell/floating-conversation';
import { FloatingReply } from '@/components/desktop-shell/floating-reply';
import {
  PinnedThreadSummary,
  useThreadSummaryLayout,
} from '@/components/desktop-shell/thread-summary';
import { useOpenAssistantPanel } from '@/features/assistants/assistant-panel-tabs';
import { useAssistantOfConversation } from '@/features/assistants/use-assistant';
import { useGroupConversation } from '@/features/plugins/plugin-conversation';
import { useMediaQuery } from '@/hooks/use-media-query';
import { messageText } from '@/lib/conversations/message-part/message-text';
import { restorePanel } from '@/lib/desktop-shell/app-panels';
import { DESKTOP_LAYOUT_QUERY } from '@/lib/desktop-shell/desktop-layout';
import { useDesktopShellStore, useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { showPanelView } from '@/lib/side-panel/panel-tabs';

/**
 * A new conversation is its composer alone, centred, with its chips (D1); before an agent is
 * found it is the agent's empty state, unless the conversations could not sync: then it stays
 * the start screen, under the sync banner (M1). A project's own conversation never is: it is
 * the project from its first moment, header and composer (N1).
 */
export function showsStartScreen({
  floating,
  project,
  agent,
  messageCount,
  syncFailed = false,
}: {
  floating: boolean;
  project: unknown;
  agent: unknown;
  messageCount: number;
  syncFailed?: boolean;
}): boolean {
  return !floating && project === null && (agent !== null || syncFailed) && messageCount === 0;
}

/**
 * The primary conversation pane: the `desktop-main` section wrapping the header,
 * inline error banners, message transcript, and composer for the active thread.
 * Each child owns a large, distinct prop surface, so they are forwarded as
 * grouped objects (typed via `ComponentProps` of each child) rather than
 * flattened — keeping this a thin, mechanical wrapper extracted from
 * `DesktopShell`'s render body.
 */
export function ConversationPane({
  chatId,
  onGroupMessagesChange,
  header,
  split = false,
  errorBanners,
  transcript,
  composer,
}: {
  chatId: string;
  onGroupMessagesChange?: (messages: CrowdexUIMessage[]) => void;
  /** The conversation's header: title, attachments, usage, panel, Summary and ⋮ (D5); `null` on a
   *  phone, whose conversation screen has its own. */
  header: ComponentProps<typeof ConversationHeader> | null;
  /** The workspace is split: this half is marked as the one whose panel shows. */
  split?: boolean;
  errorBanners: ComponentProps<typeof ConversationErrorBanners>;
  transcript: ComponentProps<typeof ConversationMessages>;
  composer: ComponentProps<typeof ConversationComposer>;
}) {
  // A group's conversation shows who said what, and sends to its members. A project's own
  // conversation is an ordinary one, led by its agent.
  const project = useAssistantOfConversation(chatId);
  const GroupConversation = useGroupConversation(composer.collaboration);
  // A project's conversation opens with its Tasks and Project tabs, unless the user arranged it.
  const openProjectPanel = useOpenAssistantPanel();
  const projectId = project?.id ?? null;
  useEffect(() => {
    if (projectId !== null) openProjectPanel(chatId, { onlyIfClosed: true });
  }, [openProjectPanel, chatId, projectId]);
  // On a phone, a project's conversation has one header: the shell's, saying the project.
  const phone = !useMediaQuery(DESKTOP_LAYOUT_QUERY);
  const floating = useFloatingConversation();
  const sectionRef = useRef<HTMLElement>(null);
  // A new conversation has no transcript: its composer is centred in the pane, its chips
  // (a repository among them, when none is chosen yet) in it. A project's own conversation is
  // the project from its first moment (N1): its header and its composer, never the start screen.
  const syncFailed = useDesktopShellStore((state) => state.workspaceSyncError !== null);
  const startScreen = showsStartScreen({
    floating,
    project,
    agent: transcript.agent ?? null,
    messageCount: transcript.displayedMessages.length,
    syncFailed,
  });
  // The summary sits beside the transcript (not over a floating composer nor a team's view);
  // a new conversation has nothing to summarise yet (D1).
  const shift = useThreadSummaryLayout(sectionRef, !floating && !GroupConversation && !startScreen);
  const store = useDesktopShellStoreApi();
  usePanelShortcut();
  const workspace = useDesktopShellStore((state) => state.workspace);
  const personalWorkspaceRoot = useDesktopShellStore((state) => state.personalWorkspaceRoot);
  // The composer's "/" commands act on this conversation (D7b).
  const repository = workspace !== null && workspace !== personalWorkspaceRoot;
  // A steer belongs to the turn it redirected: /rewind resends the message before it.
  const lastUserMessage = transcript.displayedMessages.findLast(
    (message) => message.role === 'user' && !isSteerMessage(message),
  );
  const commands: ComposerCommandRunner = {
    available: (id) =>
      id === 'rewind'
        ? lastUserMessage !== undefined && transcript.onEditMessage !== undefined
        : repository,
    run: (id) => {
      const state = store.getState();
      if (id === 'review' || id === 'pr') {
        state.setPanelTabs((tabs) => showPanelView(tabs, id === 'pr' ? 'pull-request' : 'review'));
        state.setSidePanelOpen(true);
      } else if (id === 'branch') {
        state.setNewBranchName('');
        state.setCreateBranchOpen(true);
      } else if (lastUserMessage !== undefined)
        transcript.onEditMessage?.(lastUserMessage, messageText(lastUserMessage));
    },
  };
  return (
    <ComposerCommandsProvider value={commands}>
      <section
        ref={sectionRef}
        style={{ '--thread-content-shift': `${shift}px` } as CSSProperties}
        className={
          floating
            ? 'desktop-floating-pane flex min-h-0 min-w-0 flex-col'
            : 'desktop-main relative flex h-full min-h-0 min-w-0 flex-1 flex-col bg-background'
        }
        data-conversation-pane={chatId}
        data-pip-anchor-host={floating ? undefined : chatId}
        data-panel-owner={split || undefined}
      >
        <div hidden={floating} className={floating ? 'hidden' : 'contents'}>
          {/* Split, the active conversation is edged in blue (globals.css): the panel is its own. */}
          {/* A new conversation is its composer alone (D1): it has no header yet. */}
          {header !== null && !startScreen && !(phone && project !== null) && (
            <ConversationHeader {...header} />
          )}
        </div>
        {!floating && !startScreen && (
          <ConversationFind belowHeader={header !== null} root={sectionRef} />
        )}
        <ConversationErrorBanners {...errorBanners} />
        {GroupConversation ? (
          <div
            className={
              floating
                ? 'pointer-events-auto flex max-h-[65dvh] min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-surface-solid shadow-lg'
                : 'flex min-h-0 flex-1 flex-col'
            }
          >
            <GroupConversation
              key={chatId}
              conversationId={chatId}
              initialMessages={transcript.messages}
              leadAgent={transcript.agent}
              onMessagesChange={onGroupMessagesChange}
              composer={<ConversationComposer {...composer} />}
            />
          </div>
        ) : (
          // The pinned summary spans the transcript and the composer, both moved aside for it.
          // Floating, only the latest reply shows, above the composer.
          <div className={floating ? 'contents' : 'relative flex min-h-0 flex-1 flex-col'}>
            {!floating && !startScreen && <ConversationMessages {...transcript} />}
            {floating && (
              <FloatingReply
                messages={transcript.displayedMessages}
                onOpenConversation={() => restorePanel(store)}
                status={transcript.status}
              />
            )}
            {!floating && !startScreen && <PinnedThreadSummary />}
            <ConversationComposer
              {...composer}
              conversationTitle={header?.conversationTitle}
              startScreen={startScreen}
            />
          </div>
        )}
      </section>
    </ComposerCommandsProvider>
  );
}
